import { useCreateBulletinMutation } from "entities/bulletin/mutations/useCreateBulletinMutation";
import { useUpdateBulletinMutation } from "entities/bulletin/mutations/useUpdateBulletinMutation";
import {
  invalidateBulletinDetailsQuery,
  useBulletinDetailsQuery,
} from "entities/bulletin/queries/useBulletinDetailsQuery";
import { Bulletin, BulletinDataToUpdate, BulletinDetailsTabs, BulletinStatus } from "entities/bulletin/types";
import React, { FC, PropsWithChildren, useEffect, useMemo, useState } from "react";
import useInputState, { InputHookResult } from "shared/hooks/useInputState";
import { Translations } from "shared/types/languages";
import { createContext } from "shared/utils/context";
import { useBulletinBoardPage } from "../../../context";
import { ManagedTranslations } from "../../../context/types";
import { BulletinDetailsType } from "./types";
import useRolePermission from "entities/auth/hooks/useRolePermission";

const [Provider, useBulletinDetails] = createContext<BulletinDetailsType>({
  name: "BulletinDetailsContext",
  hookName: "useBulletinDetails",
  providerName: "BulletinDetailsProvider",
});

const BulletinDetailsProvider: FC<PropsWithChildren> = (props) => {
  const { bulletinBoardDetailsDrawer } = useBulletinBoardPage();
  const [selectedTab, setSelectedTab] = useState(BulletinDetailsTabs.General);
  const [isHighPriority, setIsHighPriority] = useState(false);
  const bulletinTitle: InputHookResult = useInputState("bulletinTitle", "");
  const adminNotes: InputHookResult = useInputState("adminNotes", "");
  const bulletinDescription: InputHookResult = useInputState(
    "bulletinDescription",
    "",
  );
  const [translations, setTranslations] = useState<Translations | undefined>();

  const titleValue = bulletinTitle.input.value;
  const descriptionValue = bulletinDescription.input.value;

  const { allowed: hasReadAccess } = useRolePermission("bulletinBoard:read");
  const { allowed: hasReadTranslationsAccess } = useRolePermission("bulletinBoard:translations:read");

  const handleDrawerClose = () => {
    bulletinTitle.setValue("");
    bulletinDescription.setValue("");
    adminNotes.setValue("");
    setTranslations(undefined);
    setSelectedTab(BulletinDetailsTabs.General);
    bulletinBoardDetailsDrawer.closeDrawer();
  };

  const { data: bulletinDetails, isLoading: isLoadingBulletinDetails } =
    useBulletinDetailsQuery(bulletinBoardDetailsDrawer.drawerDetails ?? "");

  const { mutateAsync: onUpdateBulletin, isPending: isSubmittingBulletin } =
    useUpdateBulletinMutation(handleDrawerClose);

  const { mutate: createBulletin, isPending: isPendingCreateBulletin } =
    useCreateBulletinMutation(handleDrawerClose);

  useEffect(() => {
    bulletinTitle.setValue(bulletinDetails?.title ?? "");
    bulletinDescription.setValue(bulletinDetails?.description ?? "");
    adminNotes.setValue(bulletinDetails?.adminNotes ?? "");
    setIsHighPriority(bulletinDetails?.isHighPriority ?? false);
    setTranslations(bulletinDetails?.translations);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulletinDetails]);

  useEffect(() => {
    if (hasReadAccess) {
      return;
    }

    if (hasReadTranslationsAccess) {
      setSelectedTab(BulletinDetailsTabs.Translations);
    }
  }, [hasReadAccess, hasReadTranslationsAccess, bulletinDetails?.id]);

  const translationsWithDefaultValues = {
    ...translations,
    en: {
      ...translations?.en,
      [ManagedTranslations.Title]: titleValue,
    },
  };

  const onCreateBulletin = async (status: BulletinStatus) => {
    const bulletin = {
      ...bulletinDetails,
      title: titleValue,
      description: descriptionValue,
      adminNotes: adminNotes.input.value,
      isHighPriority,
      status,
      translations: translationsWithDefaultValues,
    } as Bulletin;

    return createBulletin({ data: bulletin });
  };

  const onUpdateBulletinDetails = async (status: BulletinStatus) => {
    if (!bulletinDetails?.id) {
      return;
    }

    const bulletin = {
      ...bulletinDetails,
      title: bulletinTitle.input.value,
      description: bulletinDescription.input.value,
      adminNotes: adminNotes.input.value,
      isHighPriority,
      status,
      translations: translationsWithDefaultValues,
    } as Bulletin;

    await onUpdateBulletin({ bulletin, bulletinId: bulletinDetails?.id });
    await invalidateBulletinDetailsQuery(
      bulletinBoardDetailsDrawer.drawerDetails ?? "",
    );
  };

  const disabled = useMemo(
    () =>
      !titleValue ||
      Boolean(bulletinTitle.input.errors.length) ||
      !descriptionValue ||
      Boolean(bulletinDescription.input.errors.length),
    [
      bulletinDescription.input.errors.length,
      bulletinTitle.input.errors.length,
      descriptionValue,
      titleValue,
    ],
  );

  const onSubmit = async (status: BulletinStatus) => {
    if (disabled) {
      return;
    }

    try {
      if (bulletinDetails?.id) {
        await onUpdateBulletinDetails(status);
      } else {
        await onCreateBulletin(status);
      }
    } catch (error) {
      console.error("Error submitting bulletin details:", error);
    }
  };

  const handleSaveDraft = () => onSubmit(BulletinStatus.Draft);
  const handleSaveActive = () => onSubmit(BulletinStatus.Active);
  const handleSaveTranslations = async () => {
    if (disabled) {
      return;
    }

    if (!bulletinDetails?.id) {
      return;
    }

    const bulletin = {
      translations: translationsWithDefaultValues,
    } as BulletinDataToUpdate;

    await onUpdateBulletin({ bulletin, bulletinId: bulletinDetails.id });
    await invalidateBulletinDetailsQuery(
      bulletinBoardDetailsDrawer.drawerDetails ?? "",
    );
  };

  const value = {
    bulletinDetails,
    isHighPriority,
    bulletinTitle,
    adminNotes,
    bulletinDescription,
    disabled,
    selectedTab,
    setSelectedTab,
    translations,
    setTranslations,
    handleSaveActive,
    handleSaveDraft,
    handleSaveTranslations,
    setIsHighPriority,
    isLoadingBulletinDetails,
    isSubmittingBulletin: isSubmittingBulletin || isPendingCreateBulletin,
  };

  return <Provider value={value}>{props.children}</Provider>;
};

export { BulletinDetailsProvider, useBulletinDetails };
